import React from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import { AuthProvider, useAuth } from './components/AuthContext';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#667eea',
    },
    secondary: {
      main: '#764ba2',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

const ProjectCard = ({ title, href, description, status }) => (
  <Paper
    component="a"
    href={href}
    sx={{
      p: 3,
      display: 'block',
      textDecoration: 'none',
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 6,
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
      <Typography
        variant="h6"
        sx={{
          color: 'text.primary',
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>
      <Chip
        label={status}
        size="small"
        color={status === 'Active' ? 'success' : 'default'}
        variant={status === 'Active' ? 'filled' : 'outlined'}
      />
    </Box>
    <Typography variant="body2" color="text.secondary">
      {description}
    </Typography>
  </Paper>
);

const ProjectList = () => {
  const { user, loading, login, logout } = useAuth();

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 6,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
            Dotroe
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Projects and archives.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {loading ? (
            <CircularProgress size={24} />
          ) : user ? (
            <>
              <Typography variant="body2" color="text.secondary">
                {user.name}
              </Typography>
              <Button variant="outlined" size="small" onClick={logout}>
                Sign Out
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={login}>
              Sign In
            </Button>
          )}
        </Box>
      </Box>

      <Box>
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
          Projects
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <ProjectCard
            title="Productivity"
            href="/projects/productivity/"
            description="Time tracking with recurring tasks, Pomodoro timer, and weekly schedule."
            status="Active"
          />
          <ProjectCard
            title="Reader"
            href="/projects/reader/"
            description="Royal Road novel reader with chapter tracking and RSS updates."
            status="Active"
          />
          <ProjectCard
            title="Bar Golf"
            href="/projects/bargolf/"
            description="A past Bar Golf scoreboard and photo gallery."
            status="Archived"
          />
        </Box>
      </Box>
    </Container>
  );
};

const App = () => (
  <ThemeProvider theme={darkTheme}>
    <CssBaseline />
    <AuthProvider>
      <ProjectList />
    </AuthProvider>
  </ThemeProvider>
);

export default App;
